import React, { useState, useEffect } from 'react'
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { useRecoilState } from 'recoil';
import dayjs from 'dayjs';
import {
  Button,
  Modal,
  Table,
  Upload,
  Input as AntdInput,
  Divider,
  Popover,
  Popconfirm,
  Avatar,
  Select,
  DatePicker,
} from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import {
  viewContactsAtom,
  notificationsAtom,
  editContactAtom,
  viewLeadAtom
} from '../../../../atoms/Atoms';
import api from '../../../../api/api';
import PhoneInput from '../../../shared-components/form/PhoneInput';
import { getAge, getMobileDatePickerFooter, mapContactType, toFormattedLocalDate, toFormattedLocalTime } from '../../../../functions';
import { contactTypes } from '../../../../utils';

function LeadContactsModal() {
  const { t, i18n } = useTranslation();
  const defaultFileData = {
    contacts: [],
    file: null,
    firstName: '',
    lastName: '',
    email: '',
    phone: '+1',
    birthdate: null,
    contactType: contactTypes[0].id
  };
  const { Option } = Select;
  const { Dragger } = Upload;
  const [open, setOpen] = useState(false);
  const [mainLead, setMainLead] = useRecoilState(viewLeadAtom);
  const [lead, setLead] = useRecoilState(viewContactsAtom);
  const [editContact, setEditContact] = useRecoilState(editContactAtom)
  const [contactData, setContactData] = useState(defaultFileData);
  const [loading, setLoading] = useState(false);
  const [submittingContact, setSubmittingContact] = useState(false);
  const [notifications, setNotifications] = useRecoilState(notificationsAtom);

  const draggerProps = {
    name: 'file',
    multiple: false,
    showUploadList: true,
    maxCount: 1,
    fileList: contactData.file ? [contactData.file] : [],
    beforeUpload: (file) => {
      setContactData({
        ...contactData,
        file
      });
      return false;
    },
    ondrop: (e) => {
    },
    onRemove: (e) => {
      setContactData({
        ...contactData,
        file: null
      });
    }
  };

  function checkIfContactValid() {
    const obj = {
      valid: false,
      validName: false,
      validLastName: false,
      validEmail: false,
      validPhone: false
    };
    if (contactData?.firstName?.length > 0) {
      obj.validName = true;
    }
    if (contactData?.lastName?.length > 0) {
      obj.validLastName = true;
    }
    if (contactData?.email?.length > 0) {
      obj.validEmail = true;
    }
    if (contactData?.phone?.length > 0) {
      obj.validPhone = true;
    }
    obj.valid = obj.validName && obj.validEmail && obj.validLastName && obj.validPhone;
    return obj;
  }

  function getContacts() {
    setLoading(true);
    api.get(`leads/lead/contacts/${lead?.id}`)
      .then((response) => {
        setLoading(false);
        if (response?.data?.error) {
          setNotifications([
            ...notifications,
            {
              title: t('error'),
              description: response?.data?.error,
              error: true,
            },
          ]);
        } else {
          setContactData({
            ...contactData,
            contacts: response.data.data
          })
        }
      })
      .catch((error) => {
        setLoading(false);
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: error.message,
            error: true,
          },
        ]);
      });
  }

  function upload() {
    const formdata = new FormData();
      formdata.append('file', contactData.file);
      formdata.append('phone', contactData?.phone);
      formdata.append('email', contactData?.email);
      formdata.append('firstName', contactData?.firstName);
      formdata.append('lastName', contactData?.lastName);
      formdata.append('birthdate', contactData?.birthdate);
      formdata.append('leadSubmissionId', lead?.id);
      formdata.append('contactType', lead?.contactType);
      setSubmittingContact(true);
      api.post('leads/lead/contacts', formdata)
      .then((response) => {
        setSubmittingContact(false);
        if (response?.data?.error) {
          setNotifications([
            ...notifications,
            {
              title: t('error'),
              description: response?.data?.error,
              error: true,
            },
          ]);
        } else {
          getContacts();
          setContactData({
            ...contactData,
            file: null,
            firstName: '',
            lastName: '',
            email: '',
            phone: ''
          });
        }
      })
      .catch((error) => {
        setSubmittingContact(false);
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: error.message,
            error: true,
          },
        ]);
      });
  }

  function deleteContact(id) {
    setLoading(true);
    api.delete(`leads/lead/contacts/${id}`)
      .then((response) => {
        setLoading(false);
        if (response?.data?.error) {
          setNotifications([
            ...notifications,
            {
              title: t('error'),
              description: response?.data?.error,
              error: true,
            },
          ]);
        } else {
          getContacts();
        }
      })
      .catch((error) => {
        setLoading(false);
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: error.message,
            error: true,
          },
        ]);
      });
  }

  function convertToMain(id) {
    setLoading(true);
    api.post(`leads/lead/contacts/convertomain/${id}`)
      .then((response) => {
        setLoading(false);
        if (response?.data?.error) {
          setNotifications([
            ...notifications,
            {
              title: t('error'),
              description: response?.data?.error,
              error: true,
            },
          ]);
        } else {
          getContacts();
          if (mainLead) {
            setMainLead({
              ...mainLead,
              submittedBy: response.data.data.submittedBy,
              submittedById: response.data.data.submittedById
            });
          }
          setLead({
            ...lead,
            submittedBy: response.data.data.submittedBy,
            submittedById: response.data.data.submittedById
          });
        }
      })
      .catch((error) => {
        setLoading(false);
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: error.message,
            error: true,
          },
        ]);
      });
  }

  function isMainContact(user) {
    return user.email === lead?.submittedBy?.email && user.phone === lead?.submittedBy?.phone && user.firstName === lead?.submittedBy?.firstName
  }

  useEffect(() => {
    setContactData(defaultFileData);
    if (lead) {
      setOpen(true);
      getContacts();
    } else {
      setOpen(false);
    }
  }, [lead]);

  useEffect(() => {
    if (editContact.reload) {
      setEditContact({
        ...editContact,
        reload: false
      });
      getContacts();
    }
  }, [editContact.reload]);

  return (
    <Modal
      width="80%"
      title={`${t('contacts')} ${lead?.additionalContactsCount > 0 ? `(${lead?.additionalContactsCount})` : ''}`}
      zIndex={999}
      open={open}
      onCancel={() => {
        setLead(null)
      }}
      footer={[]}
    >
      {!lead?.asSetter && (
        <>
          <div className="flex flex-col">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 mt-4 gap-2">
              <div className="col-span-1 md:col-span-2 lg:col-span-1">
                <span>{t('name')}</span>
                <AntdInput
                  className="h-14"
                  value={contactData?.firstName}
                  disabled={submittingContact}
                  onChange={(obj) => setContactData({
                    ...contactData,
                    firstName: obj.target.value
                  })}
                />
              </div>
              <div className="col-span-1 md:col-span-2 lg:col-span-1">
                <span>{t('last_name')}</span>
                <AntdInput
                  className="h-14"
                  value={contactData?.lastName}
                  disabled={submittingContact}
                  onChange={(obj) => setContactData({
                    ...contactData,
                    lastName: obj.target.value
                  })}
                />
              </div>
              <div className="col-span-1 md:col-span-2 lg:col-span-1">
                <span>{t('email')}</span>
                <AntdInput
                  className="h-14"
                  value={contactData?.email}
                  disabled={submittingContact}
                  onChange={(obj) => setContactData({
                    ...contactData,
                    email: obj.target.value
                  })}
                />
              </div>
              <div className="col-span-1 md:col-span-2 lg:col-span-1">
                <span>{t('phone')}</span>
                <PhoneInput
                  type="phone"
                  name="phone"
                  id="phone"
                  className="h-12"
                  disabled={submittingContact}
                  autoComplete="phone"
                  value={contactData?.phone}
                  onChange={(number) => {
                    setContactData({
                      ...contactData,
                      phone: number
                    });
                  }}
                />  
              </div>
              <div className="col-span-1 md:col-span-2 lg:col-span-1">
                <p>{t('birthdate')}</p>
                <DatePicker
                  className="h-12 w-full"
                  format="YYYY-MM-DD"
                  inputReadOnly
                  onChange={(date) => {
                    setContactData({
                      ...contactData,
                      birthdate: date
                    })
                  }}
                  value={contactData?.birthdate ? dayjs(contactData?.birthdate) : null}
                  showSecond={false}
                  type="primary"
                  size="small"
                  renderExtraFooter={() => getMobileDatePickerFooter(t)}
                  placement="bottomLeft"
                />
              </div>
              <div className="col-span-1 md:col-span-2 lg:col-span-1">
                <span>{t('contact_type')}</span>
                <Select
                  showSearch
                  className="w-full h-12"
                  filterOption={(input, option) => option.value.toLowerCase().includes(input.toLowerCase())}
                  value={contactData.contactType}
                  loading={loading}
                  disabled={loading}
                  onChange={(obj) => setContactData({
                    ...contactData,
                    contactType: obj
                  })}
                >
                  {_.map(contactTypes, (c) => (
                    <Option value={c.id}>{t(c.name)}</Option>
                  ))}
                </Select>
              </div>
            </div>
            <Dragger className="h-42 md:h-28 mt-5" disabled={submittingContact} {...draggerProps}>
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">
                {t('click_or_drag_content')}
              </p>
            </Dragger>
            <Popover
              content={!checkIfContactValid().valid ? (
                <>
                  {!checkIfContactValid().validName && (
                    <p>{t('must_enter_contact_name')}</p>
                  )}
                  {!checkIfContactValid().validLastName && (
                    <p>{t('must_enter_contact_last_name')}</p>
                  )}
                  {!checkIfContactValid().validEmail && (
                    <p>{t('must_enter_contact_email')}</p>
                  )}
                  {!checkIfContactValid().validPhone && (
                    <p>{t('must_enter_contact_phone')}</p>
                  )}
                </>
              ) : null}
            >
              <Button
                disabled={!checkIfContactValid().valid}
                loading={submittingContact}
                type="primary"
                className="w-2/3 md:w-1/3 mt-10 mx-auto"
                onClick={() => upload()}
              >
                {t('create')}
              </Button>
            </Popover>
          </div>
          <Divider />
        </>  
      )}
      <div className="flex">
        <Button
          size="small"
          type="primary"
          className="ml-auto mb-2"
          onClick={() => getContacts()}
        >
          {t('reload')}
        </Button>
      </div>
      <Table
        locale={{
          emptyText: t('no_data')
        }}
        bordered
        rowKey="id"
        loading={loading}
        pagination={false}
        dataSource={contactData?.contacts}
        columns={[
          {
            title: '',
            align: 'left',
            className: 'text-sm md:hidden',
            render: (row) => (
              <div>
                <p>{row?.firstName} {row?.lastName}</p>
                <p>{row?.email}</p>
                <p>{row?.phone}</p>
                <p>
                  {row?.birthdate ? `${getAge(row?.birthdate, t)} (${toFormattedLocalDate(row?.birthdate)})` : t('no_birthdate_set')}
                </p>
                <>
                  {row.idImageUrl?.length > 0 && (
                    <a href={row.idImageUrl} target="_blank" rel="noreferrer" className="underline text-blue-500">ID</a>  
                  )}
                  {!(row.idImageUrl?.length > 0) && (
                    <span>ID: N/A</span>
                  )}
                </>
                <p>{t('contact_type')}: {t(mapContactType(row?.contactType))}</p>
                <>
                  <span className="font-bold mt-2">{t('submitted_by')}: </span>
                  {row?.createdBy && (
                    <span>
                      <Avatar src={row?.createdBy?.avatar} className="bg-primaryColor h12 w-8 mr-2" />
                      {row?.createdBy?.completeName}
                    </span>
                  )}
                  {!row?.createdBy && (
                    <span>-</span>
                  )}
                </>
                <div className="grid grid-cols-2 mt-2 text-center space-x-2">
                  <Popconfirm
                    title={t('are_you_sure_delete_contact_prompt')}
                    okText={t('yes')}
                    cancelText={t('no')}
                    onConfirm={() => deleteContact(row.id)}
                  >
                    <Button
                      danger
                      disabled={lead?.asSetter}
                      type="primary"
                      size="middle"
                    >
                      {t('delete')}
                    </Button>
                  </Popconfirm>
                  <Button
                    type="primary"
                    size="middle"
                    disabled={lead?.asSetter}
                    onClick={() => setEditContact({
                      reload: false,
                      contact: row
                    })}
                  >
                    {t('edit')}
                  </Button>
                </div>
              </div>
            ),
          },
          {
            title: t('name'),
            align: 'center',
            className: 'text-sm hidden md:table-cell',
            render: (row) => (
              <span>{row?.firstName} {row?.lastName}</span>
            ),
          },
          {
            title: t('birthdate'),
            align: 'center',
            className: 'text-sm hidden md:table-cell',
            render: (row) => (
              <span>{row?.birthdate ? `${getAge(row?.birthdate, t)} (${toFormattedLocalDate(row?.birthdate)})` : t('no_birthdate_set')}</span>
            ),
          },
          {
            title: t('email'),
            align: 'center',
            className: 'text-sm hidden md:table-cell',
            render: (row) => (
              <span>{row?.email}</span>
            ),
          },
          {
            title: t('phone'),
            align: 'center',
            className: 'text-sm hidden md:table-cell',
            render: (row) => (
              <span>{row?.phone}</span>
            ),
          },
          {
            title: 'ID',
            align: 'center',
            className: 'text-sm hidden md:table-cell',
            render: (row) => (
              <>
                {row.idImageUrl?.length > 0 && (
                  <a href={row.idImageUrl} target="_blank" rel="noreferrer" className="underline text-blue-500">ID</a>  
                )}
                {!(row.idImageUrl?.length > 0) && (
                  <span>N/A</span>
                )}
              </>
            ),
          },
                    {
            title: t('contact_type'),
            align: 'center',
            className: 'text-sm hidden md:table-cell',
            render: (row) => (
              <span>{t(mapContactType(row.contactType))}</span>
            ),
          },
          {
            title: t('submitted_by'),
            align: 'center',
            className: 'text-sm hidden md:table-cell',
            render: (row) => (
              <>
                {row?.createdBy && (
                  <span>
                    <Avatar src={row?.createdBy?.avatar} className="bg-primaryColor h12 w-8 mr-2" />
                    {row?.createdBy?.completeName}
                  </span>
                )}
                {!row?.createdBy && (
                  <span>-</span>
                )}
              </>
            ),
          },
          {
            title: t('actions'),
            align: 'center',
            className: 'text-sm hidden md:table-cell',
            render: (row) => (
              <div className="flex text-center space-x-2">
                <Popconfirm
                  title={t('are_you_sure_delete_contact_prompt')}
                  okText={t('yes')}
                  cancelText={t('no')}
                  onConfirm={() => deleteContact(row.id)}
                >
                  <Button
                    danger
                    disabled={lead?.asSetter}
                    type="primary"
                    size="small"
                  >
                    {t('delete')}
                  </Button>
                </Popconfirm>
                <Button
                  type="primary"
                  size="small"
                  disabled={lead?.asSetter}
                  onClick={() => setEditContact({
                    reload: false,
                    contact: row
                  })}
                >
                  {t('edit')}
                </Button>
                <Popconfirm
                  title={t('convert_to_main_prompt')}
                  okText={t('yes')}
                  cancelText={t('no')}
                  onConfirm={() => convertToMain(row.id)}
                >
                  <Button
                    disabled={lead?.asSetter || isMainContact(row)}
                    type="primary"
                    size="small"
                  >
                    {t('convert_to_main')}
                  </Button>
                </Popconfirm>
              </div>
            ),
          },
        ]}
      />
    </Modal>
  );
}

export default LeadContactsModal;
